import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';

// Resources
import logo from '../images/logo.png';

// API/Components
import axiosInstance from '../components/axios';
import { STATE_COLOR, formatDateTime } from './enums';

// MUI Elements
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// MUI Icons
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PinDropIcon from '@mui/icons-material/PinDrop';
import PublicIcon from '@mui/icons-material/Public';
import NotificationsIcon from '@mui/icons-material/Notifications';

import HomeIcon from '@mui/icons-material/Home';

import AnalyticsIcon from '@mui/icons-material/Analytics';

import ShowChartIcon from '@mui/icons-material/ShowChart';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';

import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import BatteryStdIcon from '@mui/icons-material/BatteryStd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';

// import PieChartIcon from '@mui/icons-material/PieChart';
// import SpeedIcon from '@mui/icons-material/Speed';

// import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
// import ThermostatIcon from '@mui/icons-material/Thermostat';

// import HistoryIcon from '@mui/icons-material/History';

const measurementLinks = [
	{ id: 'System Trend', icon: <ViewCompactIcon />, link: '/trend/system/' },
	{ id: 'Snapshot', icon: <CameraAltIcon />, link: '/snapshot/' },
	{ id: 'String Trend', icon: <ViewModuleIcon />, link: '/trend/string/' },
	{ id: 'Unit Trend', icon: <BatteryStdIcon />, link: '/trend/unit/' },
];

// const realtimeLinks = [
// 	{ id: 'System', icon: <ViewCompactIcon />, link: '/realtime/system/' },
// 	{ id: 'Temperature', icon: <ThermostatIcon />, link: '/realtime/temp/' },
// 	{ id: 'String', icon: <ViewModuleIcon />, link: '/realtime/string/' },
// 	{ id: 'Unit', icon: <BatteryStdIcon />, link: '/realtime/unit/' },
// ];

const PAGE_TYPE = {
	SYSTEM: '',  // Blank for System Trend
	STRING: 'string',
	UNIT: 'unit',
	SNAPSHOT: 'snapshot',
	STATISTICS: 'stats',
	PERFORMANCE: 'performance',
};

const lightColor = {
	color: 'rgba(217, 216, 214, 0.7)',
};

const item = {
	py: '0px',
	px: 1,
	'&:hover': {
		bgcolor: 'rgba(217, 216, 214, 0.25)',
	},
	'&.Mui-selected': {
		bgcolor: '#B1282E77',
		color: '#fff',
		fontWeight: 'bold',
		'& .MuiListItemIcon-root': {
			color: '#ffffff'
		},
		'&:hover': {
			bgcolor: '#B1282E77',
		},
	}
};

const itemCategory = {
	py: 1,
	px: 1,
};

// Set the different drawer widths here
const drawerWidth = 200;
const HEADER_HEIGHT = 50;

// Local theme for navigator font and icon styling
const navTheme = createTheme({
	typography: {
		h5: {
			fontWeight: 50,
			fontSize: 26,
			letterSpacing: 0.5,
		},
	},
	shape: {
		borderRadius: 8,
	},
	mixins: {
		toolbar: {
			height: HEADER_HEIGHT,
		},
	},
	components: {
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: '#0F1820',
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
				},
				contained: {
					boxShadow: 'none',
					'&:active': {
						boxShadow: 'none',
					},
				},
			},
		},
		MuiListItemText: {
			styleOverrides: {
				primary: {
					fontSize: 14,
					fontWeight: 400,
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: 'inherit',
					minWidth: 'auto',
					marginRight: 16,
					'& svg': {
						fontSize: 20,
					},
				},
			},
		},
	},
});

// Set the minimum valid discharge event string - "1990/00/00 00:00:00"
const minDischargeEventString = 19900000000000;

// Navigation wrapper to handle the Nav collapsing on small screens
export default function Navigator(props) {
		const { mobileOpen, onDrawerToggle } = props;
		const theme = useTheme();
		const isSmUp = useMediaQuery(theme.breakpoints.up('md'));

		// Drawer logic only here; Nav returns only navigation content
		return (
			<ThemeProvider theme={navTheme}>
				<Box
					component="nav"
					sx={{
						width: { md: drawerWidth },
						flexShrink: { md: 0 },
						bgcolor: '#0F1820',
					}}
				>
					{!isSmUp && (
						<Drawer
							variant="temporary"
							open={mobileOpen}
							onClose={onDrawerToggle}
							PaperProps={{ style: { width: drawerWidth, backgroundColor: '#0F1820' } }}
						>
							<Nav />
						</Drawer>
					)}
					{isSmUp && (
						<Drawer
							variant="permanent"
							PaperProps={{ style: { width: drawerWidth, backgroundColor: '#0F1820' } }}
							sx={{ display: { md: 'block', xs: 'none' } }}
						>
							<Nav />
						</Drawer>
					)}
				</Box>
			</ThemeProvider>
		);
}

// Standard Navigation bar with icons and text
function Nav(props) {
	const { ...other } = props;
	const { locationNum } = useParams();
	const location = useLocation();
	
	const linkList = '/list';
	const linkMap = '/map';
	const linkAllEvents = '/events';
	const linkBase = '/location/' + locationNum;
	const linkAnalysis = linkBase + '/analysis/';
	const linkEvents = linkBase + '/events/';
	const linkSettings = linkBase + '/settings/email/';
	
	// Extract 14-digit event ID from the current path if on a discharge subpage
	const getCurrentEventId = () => {
		const match = location.pathname.match(/\/discharges\/(\d{14})/);
		return match ? match[1] : null;
	};
	
	// Helper for selected state: match path exactly, or if next char is ? or # (not /)
	const isSelected = (target) => {
		if (!target) return false;

		if (location.pathname === target) return true;
		
		if (location.pathname.startsWith(target)) {
				const nextChar = location.pathname.charAt(target.length);
				return nextChar === '?' || nextChar === '#' || nextChar === '';
		}
		
		return false;
	};

	// Helper for discharge subpages: match /location/:locationNum/discharges/:eventId/(subpage)
	const isDischargeSubpage = (subpage) => {
		const base = `/location/${locationNum}/discharges/`;
		const re = subpage
			? new RegExp(`^${base}\\d{14}/${subpage}(\\?|#|$)`)  // e.g. .../discharges/20250701185213/snapshot
			: new RegExp(`^${base}\\d{14}/?(\\?|#|$)`);  // e.g. .../discharges/20250701185213/ or .../discharges/20250701185213
		
		return re.test(location.pathname);
	};

	const [dischargeEvents, setDischargeEvents] = useState([]);
	const [dischargePageType, setDischargePageType] = useState(PAGE_TYPE.SYSTEM);
	const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);

	const navigate = useNavigate();

	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/discharges`).then(response => {
			const eventsData = response.data;
			setDischargeEvents(eventsData ? eventsData : []);
		});
	}, [locationNum]);

	function getEventTypeIcon(eventTypeCode) {
		switch (eventTypeCode) {
			case 0:
				return <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
			case 1:
				return <ArrowCircleDownOutlinedIcon sx={{ color: STATE_COLOR.CRITICAL }}/>
			case 2:
				return <ArrowCircleUpOutlinedIcon sx={{ color: STATE_COLOR.WITHIN_LIMITS }}/>
			default:
				return <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
		}
	}

	function convertDateTimeToLinkString(dateTime) {
		return dateTime.replace('-', '').replace('-', '').replace('T', '').replace(':', '').replace(':', '').replace('Z', '');
	}

	function handleClose() {
		setDischargeDialogOpen(false);
	};

	function handleEventTimeClick(eventDateTime) {
		handleClose();
		goToURL(eventDateTime);
	};

	function goToURL(dischargeEvent) {
		let eventString = convertDateTimeToLinkString(dischargeEvent);
		if (eventString !== 'undefined' && eventString !== 'null' && eventString !== 'NaN' && eventString > minDischargeEventString) {
			navigate('/location/' + locationNum + '/discharges/' + eventString + '/' + dischargePageType);
		}
	}

	function EventsDialog() {
		const currentEventId = getCurrentEventId();
		const showUnitCol = dischargePageType === PAGE_TYPE.SYSTEM || dischargePageType === PAGE_TYPE.STRING;

		return (
			<Dialog onClose={handleClose} open={dischargeDialogOpen}>
				<DialogTitle
					sx={{
						fontFamily: 'Saira, sans-serif',
						fontWeight: 'bold',
						textAlign: 'center',
						color: 'white',
						backgroundColor: '#0F1820',
						p: 1,
					}}
				>
					Select a Discharge Event
				</DialogTitle>
				<Divider />
				<DialogContent sx={{ p: 1 }}>
					<TableContainer sx={{ maxHeight: '75vh', overflow: 'auto', backgroundColor: '#fff' }}>
						<Table stickyHeader>
							<TableHead>
								<TableRow key='header' sx={{ backgroundColor: '#fff' }}>
									<TableCell sx={{ p: 0 }} />
									<TableCell
										key='start_datetime'
										sx={{
											fontFamily: 'Saira, sans-serif',
											fontWeight: 'bold',
											textAlign: 'center',
											color: '#0F1820',
											pt: 0.5,
											pb: 0
										}}
									>
										Start Date/Time
									</TableCell>
									<TableCell
										key='duration_text'
										sx={{
											fontFamily: 'Saira, sans-serif',
											fontWeight: 'bold',
											textAlign: 'center',
											color: '#0F1820',
											pt: 0.5,
											pb: 0
										}}
									>
										Duration
									</TableCell>
									{showUnitCol && (
										<TableCell
											key='unit_measurements'
											sx={{
												fontFamily: 'Saira, sans-serif',
												fontWeight: 'bold',
												textAlign: 'center',
												color: '#0F1820',
												pt: 0.5,
												pb: 0
											}}
										>
											Unit Meas.
										</TableCell>
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{
									dischargeEvents && dischargeEvents.length > 0 ? (
										dischargeEvents.map((dEvent, i) => {
											if (!dEvent.has_unit_measurements &&
												( dischargePageType === PAGE_TYPE.UNIT || dischargePageType === PAGE_TYPE.SNAPSHOT || dischargePageType === PAGE_TYPE.PERFORMANCE )
											) {
												return null;
											}

											// Convert event start_datetime to eventId string for comparison
											const eventId = convertDateTimeToLinkString(dEvent.start_datetime);
											const isCurrent = currentEventId === eventId;

											const tableRow = (
												<TableRow
													key={i}
													hover
													onClick={() => handleEventTimeClick(dEvent.start_datetime)}
													sx={{
														cursor: 'pointer',
														...(isCurrent ? { backgroundColor: '#B1282E77' } : {})
													}}
												>
													<TableCell key={i + '.1'} sx={{ pt: 1, pb: 0.5,}}>
														{getEventTypeIcon(dEvent.event_type)}
													</TableCell>
													<TableCell key={i + '.2'} sx={{ fontFamily: 'Saira, sans-serif', py: 0 }}>
														{formatDateTime(dEvent.start_datetime)}
													</TableCell>
													<TableCell key={i + '.3'} sx={{ fontFamily: 'Saira, sans-serif', py: 0 }}>
														{dEvent.duration_text}
													</TableCell>
													{showUnitCol && (
														<TableCell key={i + '.unit'} sx={{ pt: 1, pb: 0.5, textAlign: 'center' }}>
															{dEvent.has_unit_measurements ? (
																<CheckCircleIcon sx={{ color: '#4B9C2E99' }} />
															) : (
																<CancelIcon sx={{ color: '#F2263599' }} />
															)}
														</TableCell>
													)}
												</TableRow>
											);

											return dEvent.has_unit_measurements ? tableRow : (
												<Tooltip key={i} title="No Units Measurements recorded" enterDelay={500}>
													{tableRow}
												</Tooltip>
											);
										})
									) : (
										<TableRow>
											<TableCell colSpan={showUnitCol ? 4 : 3} sx={{ fontFamily: 'Saira, sans-serif', textAlign: 'center', py: 1}}>
												No Discharge Events Found
											</TableCell>
										</TableRow>
									)
								}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<>
			<Toolbar sx={{ minHeight: 50 }}>
				<a href="/list"><img src={logo} alt="BTECH, Inc." style={{ height: "56px" }} /></a>
			</Toolbar>
			<List disablePadding>
				<Divider />

				{/* All Locations */}
				<Box key='All Locations'>
					<ListItem sx={{ ...itemCategory, ...lightColor  }}>
						<ListItemIcon sx={{ ...lightColor }}><PublicIcon /></ListItemIcon>
						<ListItemText sx={{ ...lightColor }}>All Locations</ListItemText>
					</ListItem>
					<ListItem disablePadding key='List' sx={{ pl: 2 }}>
						<ListItemButton selected={isSelected(linkList)} to={linkList} sx={{...item, ...lightColor}}>
							<ListItemIcon><FormatListNumberedIcon /></ListItemIcon>
							<ListItemText>List View</ListItemText>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding key='Map' sx={{ pl: 2 }}>
						<ListItemButton selected={isSelected(linkMap)} to={linkMap} sx={{...item, ...lightColor}}>
							<ListItemIcon><PinDropIcon /></ListItemIcon>
							<ListItemText>Map View</ListItemText>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding key='All Events' sx={{ pl: 2 }}>
						<ListItemButton selected={isSelected(linkAllEvents)} to={linkAllEvents} sx={{...item, ...lightColor}}>
							<ListItemIcon><NotificationsIcon /></ListItemIcon>
							<ListItemText>All Events</ListItemText>
						</ListItemButton>
					</ListItem>
					<Divider sx={{ bgcolor: '#B1282E', mt: 1 }}/>
				</Box>

				{/* Battery Map */}
				<ListItemButton selected={isSelected(linkBase)} to={linkBase} sx={{ ...item, ...itemCategory, ...lightColor }}>
					<ListItemIcon>
						<HomeIcon />
					</ListItemIcon>
					<ListItemText>Battery Map</ListItemText>
				</ListItemButton>
				<Divider sx={{ bgcolor: '#B1282E' }}/>
				
				{/* Analysis Report */}
				<ListItemButton selected={isSelected(linkAnalysis)} to={linkAnalysis} sx={{ ...item, ...itemCategory, ...lightColor }}>
					<ListItemIcon>
						<AnalyticsIcon />
					</ListItemIcon>
					<ListItemText>Analysis Report</ListItemText>
				</ListItemButton>
				<Divider sx={{ bgcolor: '#B1282E' }}/>

				{/* Measurements */}
				<Box key='Measurements'>
					<ListItem sx={{ ...itemCategory, ...lightColor  }}>
						<ListItemIcon sx={{ ...lightColor }}><ShowChartIcon /></ListItemIcon>
						<ListItemText sx={{ ...lightColor }}>Measurements</ListItemText>
					</ListItem>
					{measurementLinks.map(({ id: childId, icon, link }) => {
						const fullLink = linkBase + link;
						return (
							<ListItem disablePadding key={childId} sx={{ pl: 2 }}>
								<ListItemButton selected={isSelected(fullLink)} to={fullLink} sx={{...item, ...lightColor}}>
									<ListItemIcon>{icon}</ListItemIcon>
									<ListItemText>{childId}</ListItemText>
								</ListItemButton>
							</ListItem>
						);
					})}
					<Divider sx={{ bgcolor: '#B1282E', mt: 2 }} />
				</Box>

				{/* Discharges */}
				<EventsDialog />
				<Box key='Discharges'>
					<ListItem sx={{ ...itemCategory, ...lightColor  }}>
						<ListItemIcon sx={{ ...lightColor }}><ElectricBoltIcon /></ListItemIcon>
						<ListItemText sx={{ ...lightColor }}>Discharges</ListItemText>
					</ListItem>
					<ListItem disablePadding key='Discharge Events' sx={{ pl: 2 }}>
						<ListItemButton selected={isSelected(linkBase + '/discharges/')} to={linkBase + '/discharges/'} sx={{...item, ...lightColor}}>
							<ListItemIcon><CalendarMonthIcon /></ListItemIcon>
							<ListItemText>Discharge Events</ListItemText>
						</ListItemButton>
					</ListItem>
					{/* <ListItem disablePadding key='Event Statistics' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.STATISTICS)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.STATISTICS);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><PieChartIcon /></ListItemIcon>
							<ListItemText>Event Statistics</ListItemText>
						</ListItemButton>
					</ListItem> */}
					<ListItem disablePadding key='Snapshot' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.SNAPSHOT)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.SNAPSHOT);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><CameraAltIcon /></ListItemIcon>
							<ListItemText>Snapshot</ListItemText>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding key='System Trend' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.SYSTEM)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.SYSTEM);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><ViewCompactIcon /></ListItemIcon>
							<ListItemText>System Trend</ListItemText>
						</ListItemButton>
					</ListItem>
					{/* <ListItem disablePadding key='Unit Performance' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.PERFORMANCE)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.PERFORMANCE);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><SpeedIcon /></ListItemIcon>
							<ListItemText>Unit Performance</ListItemText>
						</ListItemButton>
					</ListItem> */}
					<ListItem disablePadding key='String Trend' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.STRING)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.STRING);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><ViewModuleIcon /></ListItemIcon>
							<ListItemText>String Trend</ListItemText>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding key='Unit Trend' sx={{ pl: 2 }}>
						<ListItemButton
							selected={isDischargeSubpage(PAGE_TYPE.UNIT)}
							onClick={() => {
								setDischargePageType(PAGE_TYPE.UNIT);
								setDischargeDialogOpen(true);
							}}
							sx={{...item, ...lightColor}}
						>
							<ListItemIcon><BatteryStdIcon /></ListItemIcon>
							<ListItemText>Unit Trend</ListItemText>
						</ListItemButton>
					</ListItem>
					<Divider sx={{ bgcolor: '#B1282E', mt: 2 }} />
				</Box>

				{/* Realtime */}
				{/* <Box key='Realtime'>
					<ListItem sx={{ ...itemCategory, ...lightColor  }}>
						<ListItemIcon sx={{ ...lightColor }}><MonitorHeartIcon /></ListItemIcon>
						<ListItemText sx={{ ...lightColor }}>Realtime</ListItemText>
					</ListItem>
					{realtimeLinks.map(({ id: childId, icon, link, active }) => (
						<ListItem disablePadding key={childId} sx={{ pl: 2 }}>
							<ListItemButton selected={active} to={linkBase + link} sx={{...item, ...lightColor}}>
								<ListItemIcon>{icon}</ListItemIcon>
								<ListItemText>{childId}</ListItemText>
							</ListItemButton>
						</ListItem>
					))}
					<Divider sx={{ bgcolor: '#B1282E', mt: 2 }} />
				</Box> */}

				{/* Events */}
				<ListItemButton selected={isSelected(linkEvents)} to={linkEvents} sx={{ ...item, ...itemCategory, ...lightColor }}>
					<ListItemIcon>
						<NotificationsIcon />
					</ListItemIcon>
					<ListItemText>Events</ListItemText>
				</ListItemButton>
				<Divider sx={{ bgcolor: '#B1282E' }}/>

				{/* Unit History */}
				{/* <ListItemButton sx={{ ...item, ...itemCategory, ...lightColor }}>
					<ListItemIcon>
						<HistoryIcon />
					</ListItemIcon>
					<ListItemText>Unit History</ListItemText>
				</ListItemButton> */}
				{/* <Divider sx={{ bgcolor: '#B1282E' }}/> */}

				{/* Settings */}
				<ListItemButton selected={isSelected(linkSettings)} to={linkSettings} sx={{ ...item, ...itemCategory, ...lightColor }}>
					<ListItemIcon>
						<SettingsIcon />
					</ListItemIcon>
					<ListItemText>Email Settings</ListItemText>
				</ListItemButton>
				<Divider sx={{ bgcolor: '#B1282E' }}/>
			</List>
		</>
	);
}